import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Handshake } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { Brand } from "@/pages/Login";

export default function Reconciliation() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(sp.get("cevap") === "itiraz" ? "dispute" : "confirm");
  const [note, setNote] = useState("");
  const [claimed, setClaimed] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => { api.get(`/reconciliation/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);

  const respond = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reconciliation/${token}/respond`, { action: mode, note, claimed_balance: claimed === "" ? null : Number(claimed) });
      setDone(data.status);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const status = done || (info?.status !== "pending" ? info?.status : "");
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-[#e0f2fe] p-6">
      <div className="mx-auto max-w-lg">
        <Brand className="text-2xl" light={false} />
        <div className="fade-up mt-6 border bg-white p-6" data-testid="reconciliation-page">
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><Handshake weight="fill" className="text-[#2dd4bf]" /> Cari Mutabakat</h1>
          {err && !info && <p className="mt-4 text-sm text-red-700" data-testid="reconciliation-error">{err}</p>}
          {info && (
            <>
              <p className="mt-3 text-sm text-slate-600"><b>{info.company_name}</b> firması, <b>{info.account_name}</b> cari hesabınız için {fmtDate(info.as_of)} tarihli bakiye teyidi istiyor.</p>
              <div className="mt-4 border bg-slate-50 p-4 text-center">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Kayıtlarına göre bakiye</div>
                <div className="font-num text-3xl font-bold" data-testid="reconciliation-balance">{money(Math.abs(info.balance))}</div>
                <div className="text-xs text-slate-600">{info.balance > 0 ? "Siz borçlusunuz" : info.balance < 0 ? "Siz alacaklısınız" : "Bakiye kapalı"}</div>
              </div>
              {status ? (
                <div className={`mt-5 flex items-center gap-2 border p-4 text-sm font-semibold ${status === "confirmed" ? "border-teal-200 bg-teal-50 text-teal-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="reconciliation-done">
                  {status === "confirmed" ? <CheckCircle weight="fill" size={22} /> : <XCircle weight="fill" size={22} />}
                  {status === "confirmed" ? "Bakiyeyi onayladınız. Teşekkür ederiz." : "İtirazınız kaydedildi; firma sizinle iletişime geçecek."}
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setMode("confirm")} className={`border p-3 text-sm font-semibold ${mode === "confirm" ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200"}`} data-testid="reconciliation-mode-confirm">Onaylıyorum</button>
                    <button type="button" onClick={() => setMode("dispute")} className={`border p-3 text-sm font-semibold ${mode === "dispute" ? "border-red-600 bg-red-50 text-red-800" : "border-slate-200"}`} data-testid="reconciliation-mode-dispute">İtiraz Ediyorum</button>
                  </div>
                  {mode === "dispute" && (
                    <>
                      <Input type="number" step="0.01" placeholder="Sizin kayıtlarınızdaki bakiye (₺, isteğe bağlı)" value={claimed} onChange={(e) => setClaimed(e.target.value)} data-testid="reconciliation-claimed-input" />
                      <Textarea rows={3} placeholder="Açıklama (eksik fatura, ödeme vb.)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="reconciliation-note-input" />
                    </>
                  )}
                  {err && <p className="text-sm text-red-700" data-testid="reconciliation-error">{err}</p>}
                  <Button onClick={respond} disabled={busy} className={`w-full rounded-[4px] font-bold ${mode === "dispute" ? "bg-red-700 hover:bg-red-800" : ""}`} data-testid="reconciliation-submit-button">
                    {busy ? "Gönderiliyor…" : mode === "confirm" ? "Bakiyeyi Onayla" : "İtirazı Gönder"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
